import { Box, PropsOf } from "@chakra-ui/react"
import React, { ElementType, useContext, useState } from "react"
import { createPortal } from "react-dom"
import { Alert, AlertProps } from "./Alert"

type BannerContextType = {
  root: HTMLElement | null
}

const BannerContext = React.createContext<BannerContextType>({
  root: null,
})

type BannerProps<T extends ElementType = typeof Alert> = T extends typeof Alert
  ? {
      as?: typeof Alert
    } & Omit<AlertProps, "as">
  : { as: T; variant?: "banner" } & PropsOf<T>

export const Banner = <T extends ElementType = typeof Alert>({
  as: Component = Alert as T,
  ...props
}: BannerProps<T>) => {
  const { root } = useContext(BannerContext)

  if (Component === Alert) {
    props.variant ??= "banner"
  }

  if (!root) return <Box as={Component} {...props} />

  return createPortal(<Box as={Component} {...props} />, root)
}

export const BannerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [root, setRoot] = useState<HTMLDivElement | null>(null)

  return (
    <BannerContext.Provider value={{ root }}>
      <Box ref={setRoot} _empty={{ display: "none" }} />
      {children}
    </BannerContext.Provider>
  )
}
